import type { ConsentStatus, PixelConfig } from './types';
import { log } from './utils';

type Listener = (status: ConsentStatus) => void;

interface ConsentAdapter {
  name: string;
  /** Returns null if the adapter can't determine state yet. */
  current: () => ConsentStatus | null;
  /** Subscribe to changes. Returns an unsubscribe function. */
  subscribe: (cb: () => void) => () => void;
}

/* ─────────────── Osano ─────────────── */

/**
 * Read consent from Osano. The API surface has shifted across Osano versions
 * and tenant configurations, so this tries every documented path in order
 * from highest to lowest fidelity.
 *
 *   1. cm.storage.consent  — raw category map {ANALYTICS: 'ACCEPT', ...}
 *   2. cm.getConsent()     — returns the same map in current versions
 *   3. cm.analytics        — boolean shortcut some configs expose
 *
 * We treat the relevant categories as ANALYTICS first, then MARKETING.
 * Pixels under TDG's setup typically map to ANALYTICS in Osano's category UI.
 */
function readOsanoState(): ConsentStatus | null {
  try {
    const cm = (window as Window).Osano?.cm;
    if (!cm) return null;

    // 1. Direct storage map
    const direct = (cm as unknown as Record<string, unknown>).storage as
      | { consent?: Record<string, string> }
      | undefined;
    if (direct?.consent) {
      const v = direct.consent.ANALYTICS ?? direct.consent.MARKETING;
      if (v === 'ACCEPT') return 'granted';
      if (v === 'DENY') return 'denied';
    }

    // 2. getConsent() with no args (modern Osano)
    if (typeof cm.getConsent === 'function') {
      const c = cm.getConsent() as Record<string, string> | string | undefined;
      if (c && typeof c === 'object') {
        const v = c.ANALYTICS ?? c.MARKETING;
        if (v === 'ACCEPT') return 'granted';
        if (v === 'DENY') return 'denied';
      }
      if (typeof c === 'string') {
        return c === 'ACCEPT' ? 'granted' : 'denied';
      }
    }

    // 3. Boolean shortcut
    const bool = (cm as unknown as Record<string, unknown>).analytics;
    if (typeof bool === 'boolean') return bool ? 'granted' : 'denied';

    return null;
  } catch {
    return null;
  }
}

const osanoAdapter: ConsentAdapter | null =
  typeof window !== 'undefined' && (window as Window).Osano?.cm
    ? {
        name: 'osano',
        current: readOsanoState,
        subscribe: (cb) => {
          const cm = (window as Window).Osano!.cm;
          const handler = () => cb();
          try {
            // Osano fires these on consent state transitions. Subscribing
            // to all three covers initial-load, user-changed, and saved.
            cm.addEventListener('osano-cm-consent-changed', handler);
            cm.addEventListener('osano-cm-consent-saved', handler);
            cm.addEventListener('osano-cm-initialized', handler);
          } catch {
            /* ignore */
          }
          return () => {
            /* Osano has no removeEventListener — best-effort only */
          };
        },
      }
    : null;

/* ─────────────── OneTrust ─────────────── */

const oneTrustAdapter: ConsentAdapter | null =
  typeof window !== 'undefined' && (window as Window).OneTrust
    ? {
        name: 'onetrust',
        current: () => {
          // OneTrust uses OptanonConsent cookie; reading the "Performance" group is convention
          const m = document.cookie.match(/OptanonConsent=([^;]+)/);
          if (!m) return null;
          const decoded = decodeURIComponent(m[1]);
          // Look for groups=C0002:1 (Performance/Analytics category enabled)
          const groups = /groups=([^&]+)/.exec(decoded)?.[1] ?? '';
          if (/C0002:1/.test(groups)) return 'granted';
          if (/C0002:0/.test(groups)) return 'denied';
          return null;
        },
        subscribe: (cb) => {
          const ot = (window as Window).OneTrust!;
          if (ot.OnConsentChanged) ot.OnConsentChanged(cb);
          return () => {
            /* no remove API */
          };
        },
      }
    : null;

/* ─────────────── Cookiebot ─────────────── */

const cookiebotAdapter: ConsentAdapter | null =
  typeof window !== 'undefined' && (window as Window).Cookiebot
    ? {
        name: 'cookiebot',
        current: () => {
          const c = (window as Window).Cookiebot?.consent;
          if (!c) return null;
          return c.statistics ? 'granted' : 'denied';
        },
        subscribe: (cb) => {
          const handler = () => cb();
          window.addEventListener('CookiebotOnConsentReady', handler);
          window.addEventListener('CookiebotOnAccept', handler);
          window.addEventListener('CookiebotOnDecline', handler);
          return () => {
            window.removeEventListener('CookiebotOnConsentReady', handler);
            window.removeEventListener('CookiebotOnAccept', handler);
            window.removeEventListener('CookiebotOnDecline', handler);
          };
        },
      }
    : null;

/* ─────────────── Public API ─────────────── */

let activeAdapter: ConsentAdapter | null = null;
let manualOverride: ConsentStatus | null = null;
const listeners = new Set<Listener>();
let lastStatus: ConsentStatus = 'unknown';

function pickAdapter(): ConsentAdapter | null {
  return osanoAdapter ?? oneTrustAdapter ?? cookiebotAdapter ?? null;
}

function broadcast(): void {
  const status = currentStatus();
  if (status === lastStatus) return;
  lastStatus = status;
  log('consent →', status);
  listeners.forEach((cb) => {
    try {
      cb(status);
    } catch (e) {
      log('consent listener threw', e);
    }
  });
}

export function currentStatus(): ConsentStatus {
  if (manualOverride) return manualOverride;
  if (activeAdapter) {
    const v = activeAdapter.current();
    if (v) return v;
  }
  return 'unknown';
}

export function activeProvider(): string {
  if (manualOverride) return 'manual';
  return activeAdapter?.name ?? 'none';
}

export function initConsent(config: PixelConfig): void {
  if (config.defaultConsent === 'granted') {
    manualOverride = 'granted';
    broadcast();
    return;
  }
  if (config.defaultConsent === 'denied') {
    manualOverride = 'denied';
    broadcast();
    return;
  }
  // auto — pick the first available CMP
  activeAdapter = pickAdapter();
  if (activeAdapter) {
    log('consent adapter:', activeAdapter.name);
    activeAdapter.subscribe(broadcast);
    broadcast();
  } else {
    log('no CMP detected; consent stays unknown');
  }
}

export function grant(): void {
  manualOverride = 'granted';
  broadcast();
}

export function revoke(): void {
  manualOverride = 'denied';
  broadcast();
}

export function onChange(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
